import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { PakketConfigEditor, configNaarPayload, type PakketConfig } from '../components/PakketConfigEditor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Per-collectief instellingenscherm: pakketconfig (voorwaarden + dekking + buiten-bundel pricing)
 *  achteraf aanpassen. Spiegelt de aanmaak-wizard. */
export const AdminCollectiefConfigPage: React.FC = () => {
  const { cid } = useParams<{ cid: string }>();
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [meta, setMeta] = useState<{ name: string; slug: string; pakket_type: string } | null>(null);
  const [config, setConfig] = useState<PakketConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!cid) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/collectivities/${cid}/config`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) { notify.error('Config laden mislukt'); return; }
        setMeta({ name: d.name, slug: d.slug, pakket_type: d.pakket_type });
        const pricing = {
          kredietcheck: d.pricing.kredietcheck.length ? d.pricing.kredietcheck : d.globale_pricing.kredietcheck,
          incasso: d.pricing.incasso.length ? d.pricing.incasso : d.globale_pricing.incasso,
          verzekering: d.pricing.verzekering.length ? d.pricing.verzekering : d.globale_pricing.verzekering,
          incasso_commissie: d.pricing.incasso_commissie.length ? d.pricing.incasso_commissie : d.globale_pricing.incasso_commissie,
        };
        const v = d.voorwaarden, gv = d.globale_voorwaarden;
        const gradeCov = (d.grade_coverage && d.grade_coverage.length)
          ? d.grade_coverage
          : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((g) => ({ grade: g, max_dekking: 0 }));
        setConfig({
          pakket_type: d.pakket_type,
          polisnummer: d.polisnummer || '',
          euler_hermes_id: d.euler_hermes_id || '',
          voorwaarden: {
            max_factuur_leeftijd_dagen: v.max_factuur_leeftijd_dagen ?? gv.max_factuur_leeftijd_dagen,
            max_betalingstermijn_dagen: v.max_betalingstermijn_dagen ?? gv.max_betalingstermijn_dagen,
            overdracht_binnen_dagen: v.overdracht_binnen_dagen ?? gv.overdracht_binnen_dagen,
            overdracht_uiterlijk_dagen: v.overdracht_uiterlijk_dagen ?? gv.overdracht_uiterlijk_dagen,
          },
          dekkingspercentage: d.dekkingspercentage,
          incasso_minimum_fee: d.incasso_minimum_fee ?? d.globale_incasso_minimum_fee,
          bundel: d.bundel,
          grade_coverage: gradeCov,
          pricing,
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const opslaan = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/collectivities/${cid}/config`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(configNaarPayload(config)),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Opslaan mislukt'); }
      notify.success('Pakketconfiguratie opgeslagen');
    } catch (e) { notify.error(e instanceof Error ? e.message : 'Mislukt'); } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate('/backoffice/collectivities')} className="text-sm text-mfb-orange hover:underline mb-1">← Collectiviteiten</button>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">{meta?.name || 'Collectief'} — pakketinstellingen</h1>
          {meta && (
            <p className="text-sm text-slate-500">
              Login-code <strong>{meta.slug}</strong> ·{' '}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.pakket_type === 'kredietopinies' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {meta.pakket_type === 'kredietopinies' ? 'Kredietopinies' : 'Totaalpakket'}
              </span>
            </p>
          )}
        </div>
        <button onClick={opslaan} disabled={saving || !config}
          className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>

      {loading || !config ? (
        <div className="text-center py-12 text-slate-500">Laden…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <PakketConfigEditor config={config} onChange={setConfig} />
          <div className="mt-6 flex justify-end">
            <button onClick={opslaan} disabled={saving}
              className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminCollectiefConfigPage;
